// modules/chariot/ui/src/features/integrations/api/index.ts

/**
 * API client for integrations
 * Handles all HTTP requests to the integrations backend
 */

import axios from 'axios';

import type {
  CreateIntegrationRequest,
  IntegrationInstance,
  TestIntegrationRequest,
  TestIntegrationResponse,
  UpdateIntegrationRequest,
} from '@/features/integrations/types';

const API_BASE_URL = '/api/integrations';

/**
 * Fetch all integration instances
 */
export async function fetchIntegrations(): Promise<IntegrationInstance[]> {
  const response = await axios.get<IntegrationInstance[]>(API_BASE_URL);
  return response.data;
}

/**
 * Fetch a single integration instance by ID
 */
export async function fetchIntegration(
  id: string
): Promise<IntegrationInstance> {
  const response = await axios.get<IntegrationInstance>(
    `${API_BASE_URL}/${id}`
  );
  return response.data;
}

/**
 * Create a new integration instance
 */
export async function createIntegration(
  request: CreateIntegrationRequest
): Promise<IntegrationInstance> {
  const response = await axios.post<IntegrationInstance>(API_BASE_URL, request);
  return response.data;
}

/**
 * Update an existing integration instance
 */
export async function updateIntegration(
  request: UpdateIntegrationRequest
): Promise<IntegrationInstance> {
  const { id, ...data } = request;
  const response = await axios.put<IntegrationInstance>(
    `${API_BASE_URL}/${id}`,
    data
  );
  return response.data;
}

/**
 * Delete an integration instance
 */
export async function deleteIntegration(id: string): Promise<void> {
  await axios.delete(`${API_BASE_URL}/${id}`);
}

/**
 * Test an integration configuration before creating
 */
export async function testIntegration(
  request: TestIntegrationRequest
): Promise<TestIntegrationResponse> {
  const response = await axios.post<TestIntegrationResponse>(
    `${API_BASE_URL}/test`,
    request
  );
  return response.data;
}

/**
 * Trigger a health check for an integration instance
 */
export async function checkIntegrationHealth(
  id: string
): Promise<TestIntegrationResponse> {
  const response = await axios.post<TestIntegrationResponse>(
    `${API_BASE_URL}/${id}/health-check`
  );
  return response.data;
}

// ============================================================================
// React Query Hooks
// ============================================================================

import { queryClient } from '@/queryclient';
import { useMutation, useQuery } from '@/utils/api';

/**
 * Hook to fetch all integration instances
 */
export function useIntegrations() {
  return useQuery({
    queryKey: ['integrations'],
    queryFn: fetchIntegrations,
    defaultErrorMessage: 'Failed to load integrations',
  });
}

/**
 * Hook to fetch a single integration instance
 */
export function useIntegration(id: string) {
  return useQuery({
    queryKey: ['integrations', id],
    queryFn: () => fetchIntegration(id),
    defaultErrorMessage: 'Failed to load integration',
    enabled: !!id,
  });
}

/**
 * Hook to create a new integration
 */
export function useCreateIntegration() {
  return useMutation({
    mutationFn: createIntegration,
    defaultErrorMessage: 'Failed to create integration',
    onSuccess: () => {
      // Invalidate integrations list to refetch
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
}

/**
 * Hook to update an existing integration
 */
export function useUpdateIntegration() {
  return useMutation({
    mutationFn: updateIntegration,
    defaultErrorMessage: 'Failed to update integration',
    onSuccess: data => {
      // Invalidate both the specific integration and the list
      queryClient.invalidateQueries({
        queryKey: ['integrations', data.member],
      });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
}

/**
 * Hook to delete an integration
 */
export function useDeleteIntegration() {
  return useMutation({
    mutationFn: deleteIntegration,
    defaultErrorMessage: 'Failed to delete integration',
    onSuccess: () => {
      // Invalidate integrations list to refetch
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
}

/**
 * Hook to test integration configuration
 */
export function useTestIntegration() {
  return useMutation({
    mutationFn: testIntegration,
    defaultErrorMessage: 'Failed to test integration',
  });
}

/**
 * Hook to check integration health
 */
export function useCheckIntegrationHealth() {
  return useMutation({
    mutationFn: checkIntegrationHealth,
    defaultErrorMessage: 'Failed to check integration health',
  });
}
